import { useState, useEffect } from 'react';
import type { EmployeeData } from '@monthly-data-collection/shared';
import { avatarCacheHelpers } from '@shared/types/system-users';

interface AvatarUrlOptions {
  /**
   * Prefer thumbnail over full-size avatar
   */
  preferThumbnail?: boolean;
  
  /**
   * Maximum age in hours before considering cache stale
   */
  maxCacheAgeHours?: number;
}

interface AvatarUrlResult {
  /**
   * The resolved avatar URL to use
   */
  avatarUrl: string | undefined;
  
  /**
   * Whether the current URL is from cache (always true when available)
   */
  isCached: boolean;
  
  /**
   * Whether the current URL is a thumbnail
   */
  isThumbnail: boolean;
}

const DEFAULT_OPTIONS: Required<AvatarUrlOptions> = {
  preferThumbnail: false,
  maxCacheAgeHours: 168, // 7 days - cached avatars should be valid for longer
};

/**
 * Simplified hook for Firebase Storage avatar URL resolution
 *
 * Resolution order:
 * 1. Cached thumbnail (if preferThumbnail is true and available)
 * 2. Cached avatar (if valid and not expired)
 * 3. undefined (will show initials)
 *
 * Features:
 * - Firebase Storage cached avatars only
 * - Cache expiration handling
 * - No loading states or error handling needed
 */
export function useAvatarUrl(
  employeeData?: EmployeeData | null,
  options: AvatarUrlOptions = {}
): AvatarUrlResult {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [isCached, setIsCached] = useState(false);
  const [isThumbnail, setIsThumbnail] = useState(false);

  useEffect(() => {
    if (!employeeData) {
      setAvatarUrl(undefined);
      setIsCached(false);
      setIsThumbnail(false);
      return;
    }

    // Convert timestamp to Date if it's a string
    const timestamp = typeof employeeData.avatar_cache_timestamp === 'string'
      ? new Date(employeeData.avatar_cache_timestamp)
      : employeeData.avatar_cache_timestamp;

    // Check if cached avatars are valid (not expired)
    const isCacheValid = employeeData.avatar_cache_status === 'cached' &&
      avatarCacheHelpers.isCacheValid(timestamp, opts.maxCacheAgeHours);

    if (isCacheValid) {
      // Prefer thumbnail if requested and available
      if (opts.preferThumbnail && employeeData.cached_thumbnail_url) {
        setAvatarUrl(employeeData.cached_thumbnail_url);
        setIsCached(true);
        setIsThumbnail(true);
      } else if (employeeData.cached_avatar_url) {
        setAvatarUrl(employeeData.cached_avatar_url);
        setIsCached(true);
        setIsThumbnail(false);
      } else {
        // No cached avatar available
        setAvatarUrl(undefined);
        setIsCached(false);
        setIsThumbnail(false);
      }
    } else {
      // Cache is invalid or doesn't exist - show initials
      setAvatarUrl(undefined);
      setIsCached(false);
      setIsThumbnail(false);
    }
  }, [employeeData, opts.preferThumbnail, opts.maxCacheAgeHours]);

  return {
    avatarUrl,
    isCached,
    isThumbnail,
  };
}

/**
 * Simplified hook for basic avatar URL resolution
 * Useful for components that don't need cache status information
 */
export function useSimpleAvatarUrl(
  employeeData?: EmployeeData | null,
  preferThumbnail: boolean = false
): string | undefined {
  const { avatarUrl } = useAvatarUrl(employeeData, {
    preferThumbnail
  });
  
  return avatarUrl;
}
